import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

// ─── PROVIDERS ───────────────────────────────────────────────────────────────
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

// ─── CREATE USER PROFILE IN FIRESTORE ────────────────────────────────────────
export async function createUserProfile(user, extraData = {}) {
  const userRef = doc(db, "users", user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email || "",
      displayName: user.displayName || extraData.displayName || "",
      photoURL: user.photoURL || "",
      isGuest: false,
      privacy: "public",
      connectedAccounts: {},
      bio: "",
      onboardingComplete: false,
      createdAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
      ...extraData,
    });
  } else {
    // Update last seen
    await setDoc(userRef, { lastSeen: serverTimestamp() }, { merge: true });
  }

  return userRef;
}

// ─── EMAIL / PASSWORD ─────────────────────────────────────────────────────────
export async function signUpWithEmail(email, password, displayName) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(result.user, { displayName });
  await createUserProfile(result.user, { displayName });
  return result.user;
}

export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  await createUserProfile(result.user);
  return result.user;
}

// ─── GOOGLE ───────────────────────────────────────────────────────────────────
export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  await createUserProfile(result.user);
  return result.user;
}

// ─── APPLE ────────────────────────────────────────────────────────────────────
export async function signInWithApple() {
  const result = await signInWithPopup(auth, appleProvider);
  await createUserProfile(result.user);
  return result.user;
}

// ─── SPOTIFY (anonymous Firebase user) ───────────────────────────────────────
export async function signInWithSpotifyProfile(spotifyProfile) {
  const result = await signInAnonymously(auth);
  await createUserProfile(result.user, {
    displayName: spotifyProfile.display_name || "",
    photoURL: spotifyProfile.images?.[0]?.url || "",
    connectedAccounts: {
      spotify: {
        id: spotifyProfile.id,
        displayName: spotifyProfile.display_name,
        email: spotifyProfile.email || "",
        profileUrl: spotifyProfile.external_urls?.spotify || "",
        connected: true,
      },
    },
  });
  return result.user;
}

// ─── GUEST ────────────────────────────────────────────────────────────────────
export function continueAsGuest() {
  return { uid: "guest_" + Date.now(), isGuest: true, displayName: "Guest" };
}

// ─── SIGN OUT ─────────────────────────────────────────────────────────────────
export async function logOut() {
  await signOut(auth);
}

// ─── AUTH STATE LISTENER ──────────────────────────────────────────────────────
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ─── GET USER PROFILE ─────────────────────────────────────────────────────────
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}
